import { type FunctionComponent, useState } from 'react';

import { Button, Link, Modal } from '@dr.pogodin/react-utils';

import { useImageUrl } from 'hooks';

import { E_FILE, type ImageAssetT, P_API_ASSETS } from 'utils';

import IconCross from 'assets/images/icon-cross-24px.svg';
import IconGallery from 'assets/images/icon-dashboard-24px.svg';
import IconImage from 'assets/images/icon-image-16px.svg';

import secondaryButtonTheme from 'themes/Button/admin/secondary-v2.scss';

import S from './style.scss';

type TargetT = 'GALLERY' | 'HERO';

type PropsT = {
  addAsHero: () => void;
  addToGallery: () => void;
  numFreeGallerySlots: number;
  numGallerySlots: number;
  onClose: () => void;
  photo: File | ImageAssetT;
};

export const UsePhotoModal: FunctionComponent<PropsT> = ({
  addAsHero,
  addToGallery,
  numFreeGallerySlots,
  numGallerySlots,
  onClose,
  photo,
}) => {
  const fileImageUrl = useImageUrl(photo);

  const [target, setTarget] = useState<TargetT>('HERO');

  const url = photo instanceof File
    ? fileImageUrl
    : `${P_API_ASSETS}/${photo.id}${E_FILE}`;

  let heroOptionClass = S.option;
  if (target === 'HERO') heroOptionClass += ` ${S.selected}`;

  let galleryOptionClass = S.option;
  if (target === 'GALLERY') galleryOptionClass += ` ${S.selected}`;
  if (!numFreeGallerySlots) galleryOptionClass += ` ${S.inactive}`;

  return (
    <Modal onCancel={onClose}>
      <div styleName="content">
        <div>
          <h1>Use this photo</h1>
          <p>Where should it appear?</p>
        </div>
        <div styleName="cross">
          <Link onClick={onClose} to=""><IconCross /></Link>
        </div>
        <div styleName="preview">
          {url ? <img alt="Preview" src={url} /> : null}
          {photo instanceof File ? photo.name : photo.storageProperties.name}
        </div>
        <div styleName="options">
          <div
            className={heroOptionClass}
            onClick={() => {
              setTarget('HERO');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setTarget('HERO');
            }}
            role="button"
            tabIndex={0}
          >
            <div styleName="radio" />
            <div styleName="info">
              <h2>Profile photo</h2>
              <p>
                Hero photo at the top of your profile
              </p>
            </div>
            <IconImage styleName="iconImage" />
          </div>
          <div
            className={galleryOptionClass}
            onClick={() => {
              if (numFreeGallerySlots) setTarget('GALLERY');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && numFreeGallerySlots) {
                setTarget('GALLERY');
              }
            }}
            role="button"
            tabIndex={0}
          >
            <div styleName="radio" />
            <div styleName="info">
              <h2>Add to gallery</h2>
              <p>
                {numGallerySlots - numFreeGallerySlots} of {numGallerySlots}
                &zwnj; slots used
              </p>
            </div>
            <IconGallery styleName="iconGallery" />
          </div>
        </div>
        <div styleName="buttons">
          <Button
            onClick={onClose}
            theme={secondaryButtonTheme}
          >
            Cancel
          </Button>
          {target === 'HERO' ? (
            <Button onClick={addAsHero}>
              Add as profile photo
            </Button>
          ) : null}
          {target === 'GALLERY' ? (
            <Button
              disabled={!numFreeGallerySlots}
              onClick={addToGallery}
            >
              Add to gallery
            </Button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};
